import { Component, OnInit, ViewChild } from '@angular/core';
import { ToastService } from '../../../../shared/services/toast.service';
import {
    CsvUploadService,
    ISavedCsvUploadStatus,
    IUploadTypeInfo,
    UploadType,
} from 'app/modules/settings/services/csv-upload.service';
import { saveAs } from 'file-saver';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ITableColumns } from 'app/modules/reports/interfaces/reporting-data';
import {
    ICellClickEventInfo,
    ICustomTableInfoExtend,
} from 'app/modules/shared/components/custom-mat-table/custom-mat-table.component';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';
import { CustomMatTableService } from 'app/modules/shared/services/custom-mat-table.service';
import { MatDialog } from '@angular/material/dialog';
import {
    DynamicDialogComponent,
    IDynamicDialogData,
} from 'app/modules/shared/components/dynamic-dialog/dynamic-dialog.component';

@Component({
    selector: 'file-upload-integration',
    templateUrl: './file-upload-integration.component.html',
    styleUrls: ['./file-upload-integration.component.scss'],
})
export class FileUploadIntegrationComponent implements OnInit {
    constructor(
        private _toastService: ToastService,
        private _csvUploadService: CsvUploadService,
        private _reusableFunctions: ReusableFunctions,
        private _dialog: MatDialog,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private _customMatTableService: CustomMatTableService
    ) {}

    uploadBranchesUrl = 'api/v0.5/csvupload/branch';
    uploadEmployeesUrl = 'employee';
    uploadGeneralLedgerUrl = 'api/v0.5/csvupload/glaccount';
    requiredFileType: string;
    fileManagementTabs: string[];
    tabIndex: number;
    logTabIndex: number;
    fileUploadTypeLogTab: IUploadTypeInfo[];
    columnInfoList: ITableColumns[];
    customTableInfo: ICustomTableInfoExtend;
    searchTerm: string;
    action: NoDataActionType;

    uploadOptions = [
        {
            name: 'hierarchyUpload',
            uploading: false,
            downloading: false,
            label: 'Hierarchy',
            url: 'branch',
            fileName: 'MA_Hierarchy',
        },
        {
            name: 'employeeUpload',
            uploading: false,
            downloading: false,
            label: 'Employee',
            url: 'employee',
            fileName: 'MA_Employee',
        },
        {
            name: 'glUpload',
            uploading: false,
            downloading: false,
            label: 'General Ledger',
            url: 'GeneralLedger',
            fileName: 'MA_GeneralLedger',
        },
        {
            name: 'productMappingUpload',
            uploading: false,
            downloading: false,
            label: 'Product Mapping',
            url: 'ProductMapping',
            fileName: 'MA_ProductMapping',
        },
    ];

    @ViewChild('uploadElement') uploadForm: any;

    ngOnInit() {
        this.tabIndex = 0;
        this.logTabIndex = 0;
        this.requiredFileType = '.csv';
        this.fileManagementTabs = ['File Management', 'Logs'];

        /*
        Add to fileUploadTypeLogTab when supported:

            {
                index: 0,
                title: 'Hierarchy',
                uploadType: 'Hierarchy',
                isUploaded: false,
            },
            {
                index: 1,
                title: 'Employees',
                uploadType: 'Employees',
                isUploaded: false,
            },
         */

        this.fileUploadTypeLogTab = [
            {
                index: 0,
                title: 'General Ledger',
                uploadType: 'GeneralLedger',
                isUploaded: false,
            },
            {
                index: 1,
                title: 'Product Mapping',
                uploadType: 'ProductMapping',
                isUploaded: false,
            },
        ];

        this.searchTerm = '';
        this.action = NoDataActionType.pageStart;
        this.initiateTableProperties();
    }

    initiateTableProperties() {
        this.columnInfoList = [
            {
                fieldId: 'insertTime',
                fieldName: 'Time',
                dataType: 'dateTime',
                fieldOrder: 0,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                width: 200,
            },
            {
                fieldId: 'fileName',
                fieldName: 'File Name',
                fieldOrder: 1,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                width: 300,
                dataType: 'string',
            },
            {
                fieldId: 'rowNumber',
                fieldName: 'Row #',
                fieldOrder: 2,
                isSortable: true,
                headerAlignment: 'right',
                dataAlignment: 'right',
                width: 75,
                dataType: 'number',
            },
            {
                fieldId: 'initialCheckStatus',
                fieldName: 'Pre-upload status',
                fieldOrder: 3,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                width: 120,
                dataType: 'string',
            },
            {
                fieldId: 'uploadStatus',
                fieldName: 'Post-upload Status',
                fieldOrder: 4,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                width: 120,
                dataType: 'string',
            },
            {
                fieldId: 'reason',
                fieldName: 'Failure Reason',
                fieldOrder: 5,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                width: 350,
                dataType: 'string',
            },
            {
                fieldId: 'records',
                fieldName: 'Records',
                isHyperlink: true,
                fieldOrder: 6,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                width: 120,
                dataType: 'string',
                cellClickEventType: 'modelOpen',
            },
        ];

        this.customTableInfo = {
            tableId: 'file-upload-log-table',
            tableHeight: 600,
            hasData: false,
            isDataReady: false,
            columnInfoList: this.columnInfoList,
            dataSet: [],
            searchTerm: this.searchTerm,
            action: this.action,
            title: 'File Upload History',
            bandColorList: [],
            dataHandleInUiSide: true,
            showSummary: false,
        };
    }

    cellClickedEvent(event: ICellClickEventInfo) {
        let dataString = event?.cellData['uploadData'];
        if (event?.columnInfo?.cellClickEventType === 'modelOpen' && dataString?.length > 0) {
            let tableColumns: ITableColumns[] = [
                {
                    fieldId: 'key',
                    fieldName: 'Key',
                    dataType: 'string',
                    fieldOrder: 0,
                    dataAlignment: 'left',
                    isSortable: true,
                    headerAlignment: 'left',
                },
                {
                    fieldId: 'value',
                    fieldName: 'Value',
                    dataType: 'string',
                    fieldOrder: 1,
                    dataAlignment: 'left',
                    isSortable: true,
                    headerAlignment: 'left',
                },
            ];

            let popupDialogData = [];

            try {
                let cellData = JSON.parse(dataString);
                cellData.forEach((item) => {
                    popupDialogData.push({
                        key: item['ColumnName'],
                        value: item['Value'],
                    });
                });
            } catch (e) {
                this._toastService.warning('Error reading record data');
            }

            this._dialog.open(DynamicDialogComponent, {
                data: {
                    columnList: tableColumns,
                    tableData: popupDialogData,
                } as IDynamicDialogData,
                minWidth: '250px',
                minHeight: '200px',
                maxHeight: '500px',
                maxWidth: '500px',
            });
        }
    }

    handleLogTabChange(event: number) {
        let selectedTab = this.fileUploadTypeLogTab.find((t) => t.index === event);
        //TODO: Minimize redundancy of code

        this.logTabIndex = event;
        if (selectedTab) {
            this.customTableInfo.dataSet = [];
            this.customTableInfo = this._customMatTableService.refreshCustomMatTable(
                this.customTableInfo,
                false,
                false,
                this.action,
                this.searchTerm
            );

            this._csvUploadService.getFileUploadStatus(selectedTab.uploadType).subscribe(
                (res) => {
                    if (res && res.length > 0) {
                        this.setLogTableData(res);

                        this.customTableInfo.dataSet = res;
                        this.customTableInfo = this._customMatTableService.refreshCustomMatTable(
                            this.customTableInfo,
                            true,
                            true,
                            this.action,
                            this.searchTerm
                        );
                    } else {
                        this._toastService.info('No data available');

                        this.customTableInfo.dataSet = [];
                        this.customTableInfo = this._customMatTableService.refreshCustomMatTable(
                            this.customTableInfo,
                            false,
                            true,
                            this.action,
                            this.searchTerm
                        );
                    }
                },
                (error) => {
                    this._toastService.warning('Unable to find data');

                    this.customTableInfo.dataSet = [];
                    this.customTableInfo = this._customMatTableService.refreshCustomMatTable(
                        this.customTableInfo,
                        false,
                        true,
                        NoDataActionType.error,
                        this.searchTerm
                    );
                }
            );
        }
    }

    setLogTableData(logData: ISavedCsvUploadStatus[]) {
        logData.map((log) => {
            let preStatus = log['initialCheckStatus'];
            let postStatus = log['uploadStatus'];
            if (
                (preStatus && preStatus.toLowerCase() === 'failed') ||
                (postStatus &&
                    (postStatus.toLowerCase() === 'failed' ||
                        postStatus.toLowerCase() === 'success'))
            ) {
                log['records'] = 'View data';
            } else {
                log['records'] = '';
            }
        });
    }

    getLogTab(uploadType: UploadType) {
        return this.fileUploadTypeLogTab.find((tab) => tab.uploadType === uploadType);
    }

    confirmDownloadType(event: any, uploadOption: any) {
        this.confirmationService.confirm({
            target: event.target,
            message: 'Would you like to download with existing data or an empty template file?',
            acceptLabel: 'Data',
            rejectLabel: 'Template',
            rejectButtonProps: { severity: 'secondary' },
            position: 'top-center',
            accept: () => {
                this.onDownload(uploadOption, true);
            },
            reject: () => {
                this.onDownload(uploadOption, false);
            },
        });
    }

    // Has to support new (ProductMapping,GL) and old (Emp,Hierarchy) way of handling download
    onDownload(uploadOption: any, returnData: boolean) {
        uploadOption.downloading = true;

        let dateTime = this._reusableFunctions.getCurrentDateTimeStamp();
        let fileName = '';

        if (returnData) {
            fileName = uploadOption.fileName.toString() + '_' + dateTime + '.csv';
        } else {
            fileName = uploadOption.fileName.toString() + '_template.csv';
        }

        if (uploadOption.name === 'hierarchyUpload' || uploadOption.name == 'employeeUpload') {
            this._csvUploadService.downloadCsvFile(uploadOption.url, returnData).subscribe({
                next: (res) => {
                    if (res) {
                        saveAs(res, fileName);
                        uploadOption.downloading = false;
                    }
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error downloading file',
                        detail: `Something went wrong downloading ${uploadOption.label} data`,
                        life: 10000,
                    });

                    uploadOption.downloading = false;
                },
            });
        } else {
            if (uploadOption.name == 'productMappingUpload') {
                this.getCurrentlyAvailableData('ProductMapping', returnData, fileName);
            } else if (uploadOption.name == 'glUpload') {
                this.getCurrentlyAvailableData('GeneralLedger', returnData, fileName);
            }
        }
    }

    getCurrentlyAvailableData(uploadType: UploadType, returnData: boolean, fileName: string) {
        this._csvUploadService
            .getCurrentUploadedDataAsCsv(uploadType, fileName, returnData)
            .subscribe((res) => {
                if (res) {
                    saveAs(res, fileName);
                } else {
                    this._toastService.warning('Unable to download data');
                }

                this.uploadOptions.forEach((x) => (x.downloading = false));
            });
    }

    onUpload(event: any, uploadOption: any, elementRef: any) {
        let file = event.files[0];

        if (!file) {
            return;
        }

        uploadOption.uploading = true;

        if (uploadOption.name === 'hierarchyUpload' || uploadOption.name == 'employeeUpload') {
            this._csvUploadService.uploadFile(file, uploadOption.url).subscribe({
                next: (response) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: `${uploadOption.label} CSV has been sent off for processing`,
                    });

                    uploadOption.uploading = false;
                    elementRef.clear();
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error uploading file',
                        detail: error.error,
                        life: 10000,
                    });

                    uploadOption.uploading = false;
                },
            });
        } else {
            let formData = new FormData();
            formData.append('file', file);
            this._csvUploadService.uploadCsv(uploadOption.url, formData).subscribe({
                next: (response) => {
                    if (response && response.isHeaderValid) {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: `${uploadOption.label} CSV has been sent off for processing. ${response.dataMessage}`,
                            life: 10000,
                        });
                    } else {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error uploading file',
                            detail: response.dataMessage,
                            life: 10000,
                        });
                    }

                    elementRef.clear();
                    uploadOption.uploading = false;
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error uploading file',
                        detail: error.error,
                        life: 10000,
                    });

                    uploadOption.uploading = false;
                },
            });
        }
    }
}
